'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import type { CSSProperties } from 'react';
import { useAuthUser } from '@/hooks/use-auth-user';
import { useIsNativeApp } from '@/hooks/use-native-app';
import { E, editorBandSurface } from '@/lib/editorial';

const bandRule = 'rgba(245,242,237,0.2)';

const footLabel: CSSProperties = {
  fontFamily: "'Halyard Display', system-ui, sans-serif",
  fontSize: 11,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: E.gold,
  marginBottom: 14,
};

function FootLink({
  href,
  children,
  external,
}: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
}) {
  const className =
    'inline-block text-[14px] leading-snug tracking-wide border-b border-transparent pb-0.5 ' +
    'text-[rgba(245,242,237,0.88)] transition-[color,border-color] duration-200 ' +
    'hover:text-[#daba7b] hover:border-[#daba7b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#daba7b]';

  if (external) {
    return (
      <a href={href} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}

export default function Footer() {
  const pathname = usePathname();
  const isHotelsPage = pathname?.startsWith('/hotels') || pathname?.startsWith('/bookings/hotels');
  const authUser = useAuthUser();
  const user = authUser ?? null;
  const isLoading = authUser === undefined;
  const year = React.useMemo(() => new Date().getFullYear(), []);
  const isNative = useIsNativeApp();

  // The app has the tab bar and the account menu; the footer is the marketing site's.
  if (isNative) return null;

  // Signed-in users on mobile get the bottom tab bar instead; every footer
  // link except Terms/Privacy/LinkedIn duplicates the sheet or tab bar, and
  // those three move into the tab bar's Account menu. The footer stays for
  // visitors (marketing) and md+ (no tab bar there).
  return (
    <footer className={user ? 'mt-auto hidden w-full md:block' : 'mt-auto w-full'}>
      <div className="e-pad" style={editorBandSurface}>
        <div style={{ maxWidth: 1280, margin: '0 auto', width: '100%' }}>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,200px)_1fr] md:gap-14 lg:gap-20">
            {/* Brand */}
            <div className="flex flex-col gap-4">
              <Link href="/" className="inline-block w-fit opacity-95 transition-opacity hover:opacity-100">
                <Image
                  src="/nl-logo-vertical.webp"
                  alt="Nordic LuxAdvisor"
                  width={200}
                  height={250}
                  className="h-auto w-[140px] sm:w-[160px] md:w-[180px]"
                />
              </Link>
              <p
                style={{
                  fontFamily: E.serif,
                  fontSize: 15,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  lineHeight: 1.45,
                  color: 'rgba(245,242,237,0.72)',
                  margin: 0,
                  maxWidth: 280,
                }}
              >
                Privileged rates, curated experiences — at market price.
              </p>
            </div>

            {/* Nav */}
            <div className="grid grid-cols-1 gap-8 border-t border-white/20 pt-7 sm:grid-cols-2 sm:pt-8 lg:grid-cols-3 lg:gap-12 md:border-t-0 md:pt-0">
              <nav className="flex flex-col gap-2.5">
                <div style={footLabel}>Explore</div>
                {!isLoading && user ? (
                  <>
                    <FootLink href="/hotels">Hotel booking</FootLink>
                    <FootLink href="/initiatives">Curated experiences</FootLink>
                    <FootLink href="/services">Concierge services</FootLink>
                  </>
                ) : (
                  <>
                    <FootLink href="/services">Our services</FootLink>
                    <FootLink href="/membership">Membership</FootLink>
                    <FootLink href="/hotels">Hotels</FootLink>
                  </>
                )}
              </nav>

              <nav className="flex flex-col gap-2.5">
                <div style={footLabel}>Company</div>
                <FootLink href="/about">About us</FootLink>
                <FootLink href="/contact">Contact & inquiries</FootLink>
                <FootLink href="https://www.linkedin.com/company/nordic-luxadvisor" external>
                  LinkedIn
                </FootLink>
              </nav>

              <nav className="flex flex-col gap-2.5 sm:col-span-2 lg:col-span-1">
                <div style={footLabel}>Legal</div>
                <FootLink href="/terms">Terms of use</FootLink>
                <FootLink href="/privacy">Privacy policy</FootLink>
              </nav>
            </div>
          </div>

          <div
            className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t pt-6"
            style={{ borderColor: bandRule }}
          >
            <p
              style={{
                ...E.cap,
                fontSize: 10,
                letterSpacing: '0.2em',
                color: 'rgba(245,242,237,0.45)',
                margin: 0,
              }}
            >
              © {year} Nordic LuxAdvisor
            </p>
            {isHotelsPage ? (
              <p
                style={{
                  ...E.cap,
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  color: 'rgba(245,242,237,0.55)',
                  margin: 0,
                }}
              >
                Powered by Little Emperors
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}
